<template>
  <div class="serial-stock-report">
    <v-card flat class="report-page-card">
      <ReportViewBar
        :title="`${selectedStatusLabel} Serial Stock`"
        :export-data="exportData"
        :export-fields="exportFields"
      />

      <v-card-text class="report-content">
        <v-card flat outlined class="report-table-card">
          <v-card-title class="report-table-header">
            <div>
              <div class="report-title">{{ selectedStatusLabel }} Serial Stock</div>
              <div class="report-subtitle">
                {{ filteredProducts.length }} products found
              </div>
            </div>

            <v-spacer />

            <div class="summary-box">
              <div class="summary-label">Stock</div>
              <div class="summary-value">{{ totalQty }}</div>
            </div>

            <div v-if="!hideCost" class="summary-box">
              <div class="summary-label">Total Cost</div>
              <div class="summary-value">{{ total | currency }}</div>
            </div>
          </v-card-title>

          <v-divider />

          <v-card-text class="report-controls no-print">
            <v-row align="center">
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="search"
                  outlined
                  prepend-inner-icon="mdi-magnify"
                  rounded
                  dense
                  label="Search product or ID"
                  single-line
                  hide-details
                  clearable
                />
              </v-col>

              <v-col cols="12" md="4">
                <div class="status-label">Status</div>
                <v-chip-group
                  v-model="status"
                  mandatory
                  active-class="primary white--text"
                >
                  <v-chip small>Available</v-chip>
                  <v-chip small>On Hold</v-chip>
                  <v-chip small>Repair</v-chip>
                </v-chip-group>
              </v-col>

              <v-col cols="12" md="2" class="text-md-right">
                <v-checkbox
                  v-model="hideCost"
                  label="Hide Cost"
                  dense
                  hide-details
                  class="mt-0"
                />
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider />

          <v-card-text class="pa-0">
            <v-progress-linear
              v-if="isLoading || localLoading"
              indeterminate
              height="3"
            />

            <v-data-table
              v-else
              dense
              sort-by="name"
              :mobile-breakpoint="0"
              :headers="visibleHeaders"
              :expanded.sync="expanded"
              show-expand
              :items-per-page="-1"
              hide-default-footer
              :items="filteredProducts"
              class="serial-stock-table"
            >
              <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
                <v-btn class="no-print" icon small @click="expand(!isExpanded)">
                  <v-icon v-if="isExpanded">mdi-chevron-up</v-icon>
                  <v-icon v-else>mdi-chevron-down</v-icon>
                </v-btn>
              </template>

              <template v-slot:item.localId="{ item }">
                <span class="font-weight-medium">
                  {{ item.localId }}
                </span>
              </template>

              <template v-slot:item.name="{ item }">
                <span class="text-capitalize font-weight-medium">
                  {{ item.name }}
                </span>
              </template>

              <template v-slot:item.serialCount="{ item }">
                <span class="stock-count">
                  {{ item.serialCount }}
                </span>
              </template>

              <template v-slot:item.price="{ item }">
                <span class="money">
                  {{ item.price | currency }}
                </span>
              </template>

              <template v-slot:item.avgCost="{ item }">
                <span class="money">
                  {{ getAvgCost(item) | currency }}
                </span>
              </template>

              <template v-slot:item.totalCost="{ item }">
                <span class="money">
                  {{ item.totalCost | currency }}
                </span>
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-0">
                  <div class="expanded-panel">
                    <PurchaseDetailList
                      :ProductId="item.id"
                      :status="selectedStatus"
                    />
                  </div>
                </td>
              </template>

              <template v-slot:body.append v-if="filteredProducts.length > 0">
                <tr class="total-row">
                  <td></td>
                  <td colspan="2" class="text-right font-weight-bold">
                    Total
                  </td>
                  <td class="text-center font-weight-bold">
                    {{ totalQty }}
                  </td>
                  <td></td>
                  <td v-if="!hideCost"></td>
                  <td v-if="!hideCost" class="text-right font-weight-bold">
                    {{ total | currency }}
                  </td>
                </tr>
              </template>

              <template v-slot:no-data>
                <div class="empty-state">
                  No serial stock found.
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import ReportViewBar from "@/components/ReportViewBar";
import PurchaseDetailList from "@/modules/Inventory/Purchase/components/PurchaseDetailList";
import reportService from "@/modules/ReportView/service.js";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "serial-stock-report",

  components: {
    ReportViewBar,
    PurchaseDetailList,
  },

  data() {
    return {
      expanded: [],
      localLoading: false,
      status: 0,
      search: "",
      isLoading: true,
      Inventory: [],
      hideCost: false,
      headers: [
        {
          text: "",
          align: "left",
          value: "data-table-expand",
          sortable: false,
          width: 60,
        },
        {
          text: "ID",
          align: "left",
          value: "localId",
          sortable: true,
        },
        {
          text: "Product Name",
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: "Stock",
          align: "center",
          value: "serialCount",
          sortable: true,
        },
        {
          text: "Sale Price",
          align: "right",
          value: "price",
          sortable: true,
        },
        {
          text: "Avg Unit Cost",
          align: "right",
          value: "avgCost",
          sortable: true,
        },
        {
          text: "Total Cost",
          align: "right",
          value: "totalCost",
          sortable: true,
        },
      ],
    };
  },

  computed: {
    ...mapGetters("global", ["currentBusiness"]),

    selectedStatus() {
      if (this.status === 0) return "available";
      if (this.status === 1) return "onHold";
      if (this.status === 2) return "repair";

      return "available";
    },

    selectedStatusLabel() {
      if (this.status === 0) return "Available";
      if (this.status === 1) return "On Hold";
      if (this.status === 2) return "Repair";

      return "Available";
    },

    visibleHeaders() {
      if (!this.hideCost) return this.headers;

      return this.headers.filter(
        (header) =>
          header.value !== "avgCost" && header.value !== "totalCost"
      );
    },

    filteredProducts() {
      if (!this.Inventory) return [];

      if (!this.search) return this.Inventory;

      const searchTerms = this.search.toLowerCase().split(" ");

      return this.Inventory.filter((product) =>
        searchTerms.every(
          (term) =>
            (product.name && product.name.toLowerCase().includes(term)) ||
            (product.localId &&
              product.localId.toString().toLowerCase().includes(term))
        )
      );
    },

    total() {
      return this.filteredProducts.reduce(
        (sum, row) => sum + Number(row.totalCost || 0),
        0
      );
    },

    totalQty() {
      return this.filteredProducts.reduce(
        (sum, row) => sum + Number(row.serialCount || 0),
        0
      );
    },

    exportFields() {
      if (this.hideCost) {
        return {
          ID: "localId",
          "Product Name": "name",
          Stock: "serialCount",
          "Sale Price": "price",
        };
      }

      return {
        ID: "localId",
        "Product Name": "name",
        Stock: "serialCount",
        "Sale Price": "price",
        "Avg Unit Cost": "avgCost",
        "Total Cost": "totalCost",
      };
    },

    exportData() {
      return this.filteredProducts.map((row) => ({
        localId: row.localId,
        name: row.name,
        serialCount: Number(row.serialCount || 0),
        price: Number(row.price || 0),
        avgCost: this.getAvgCost(row),
        totalCost: Number(row.totalCost || 0),
      }));
    },
  },

  created() {
    this.loadData();

    if (!this.currentBusiness) {
      this.initUser();
      this.initBusiness();
    }
  },

  methods: {
    ...mapActions("global", ["initUser", "initBusiness"]),

    getAvgCost(item) {
      const count = Number(item.serialCount || 0);
      if (!count) return 0;

      return Number(item.totalCost || 0) / count;
    },

    async loadData() {
      this.localLoading = true;

      try {
        const response = await reportService.getAvailableSerial({
          status: this.selectedStatus,
        });

        this.Inventory = response.data || [];
      } finally {
        this.$Progress.finish();
        this.isLoading = false;
        this.localLoading = false;
      }
    },
  },

  watch: {
    status() {
      this.expanded = [];
      this.loadData();
    },
  },
};
</script>

<style scoped>
.serial-stock-report {
  background: #f5f7fb;
  min-height: 100vh;
  padding: 16px;
}

.report-page-card {
  border-radius: 18px;
  overflow: hidden;
}

.report-content {
  padding: 20px;
}

.report-table-card {
  border-radius: 16px;
  overflow: hidden;
}

.report-table-header {
  background: #fff;
  padding: 18px 20px;
}

.report-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.report-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 3px;
}

.summary-box {
  min-width: 120px;
  padding: 8px 12px;
  margin-left: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  text-align: right;
  background: #f9fafb;
}

.summary-label {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
}

.summary-value {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.report-controls {
  background: #fff;
  padding: 16px 20px;
}

.status-label {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  margin-bottom: 6px;
}

.stock-count {
  font-weight: 700;
}

.money {
  font-weight: 700;
}

.expanded-panel {
  padding: 14px;
  background: #f3f8fc;
  border-top: 1px solid #e5e7eb;
}

.total-row {
  background: #f9fafb;
}

.empty-state {
  padding: 32px;
  text-align: center;
  color: #6b7280;
}

.serial-stock-table ::v-deep th {
  font-size: 12px;
  font-weight: 700;
  color: #374151 !important;
  background: #f9fafb;
  text-transform: uppercase;
}

.serial-stock-table ::v-deep td {
  font-size: 13px;
}

@media print {
  .serial-stock-report {
    background: #fff;
    padding: 0;
  }

  .report-page-card,
  .report-table-card {
    box-shadow: none !important;
    border-radius: 0;
  }

  .report-content {
    padding: 0;
  }

  .summary-box {
    border: none;
    background: #fff;
  }

  .report-controls,
  .no-print {
    display: none !important;
  }
}
</style>